import { Bar, BarChart, CartesianGrid, Cell, LabelList, XAxis, YAxis, Tooltip } from "recharts"
import {
  ChartConfig,
  ChartContainer,
} from "@components/common/chart"

// ---------------------------------------------------------------------------
// AssigneesBarChart — horizontal bars (RTL-aware)
// ---------------------------------------------------------------------------

interface TaskInfo {
  title: string
  status: string
  priority: string
}

export interface AssigneesBarChartProps {
  data: { name: string; count: number; fill: string; tasks?: TaskInfo[] }[]
  isHebrew: boolean
  translate: (key: string) => string
  onBarClick?: (entry: { name: string; count: number; fill: string }, index: number) => void
}

function WorkloadTooltip({ active, payload, translate }: any) {
  if (!active || !payload?.length) return null
  const entry = payload[0]?.payload
  if (!entry) return null

  return (
    <div
      dir={translate ? "rtl" : "ltr"}
      className="rounded-lg border bg-white px-3 py-2 shadow-lg"
      style={{ minWidth: 200, maxWidth: 320 }}
    >
      <p className="mb-1.5 text-sm font-semibold">{entry.name}</p>
      <p className="mb-2 text-xs text-muted-foreground">
        {entry.count} {translate?.("tasks") || "tasks"}
      </p>
      {entry.tasks?.length > 0 && (
        <div className="space-y-1.5 border-t pt-1.5">
          {entry.tasks.map((t: TaskInfo, i: number) => (
            <div key={i} className="flex items-start gap-2 text-xs">
              <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{t.title}</p>
                <div className="flex gap-2 text-muted-foreground">
                  {t.status && <span>{t.status}</span>}
                  {t.priority && <span>• {t.priority}</span>}
                </div>
              </div>
            </div>
          ))}
          {entry.count > 5 && (
            <p className="text-[10px] text-muted-foreground">
              +{entry.count - 5} {translate?.("more") || "more"}...
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export function AssigneesBarChart({
  data,
  isHebrew,
  translate,
  onBarClick,
}: AssigneesBarChartProps) {
  const config: ChartConfig = {
    count: { label: translate("Tasks"), color: "hsl(var(--chart-1))" },
  }
  data.forEach((d) => {
    config[d.name] = { label: d.name, color: d.fill }
  })

  return (
    <ChartContainer config={config} className="w-full" style={{ height: Math.max(280, data.length * 36), direction: "ltr" }}>
      <BarChart data={data} layout="vertical" margin={{ top: 8, right: isHebrew ? 8 : 40, left: isHebrew ? 40 : 0, bottom: 0 }}>
        <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis
          type="number"
          tickLine={false}
          axisLine={false}
          tick={{ fontSize: 11, fill: "#6b7280" }}
          allowDecimals={false}
        />
        <YAxis
          type="category"
          dataKey="name"
          tickLine={false}
          axisLine={false}
          orientation={isHebrew ? "right" : "left"}
          tick={{ fontSize: 12, fill: "#374151", textAnchor: isHebrew ? "start" : "end" }}
          width={180}
        />
        <Tooltip
          cursor={{ fill: "rgba(0,0,0,0.04)" }}
          content={<WorkloadTooltip translate={translate} />}
        />
        <Bar
          dataKey="count"
          radius={[0, 6, 6, 0]}
          barSize={24}
          aria-label="Assignee task count"
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={entry.fill}
              onClick={onBarClick ? () => onBarClick(entry, index) : undefined}
              style={onBarClick ? { cursor: "pointer" } : undefined}
            />
          ))}
          <LabelList
            dataKey="count"
            position="right"
            style={{ fontSize: 11, fontWeight: 600, fill: "#374151" }}
          />
        </Bar>
      </BarChart>
    </ChartContainer>
  )
}
